// Centralized auth redirect helper
// Stores an error message and the intended path, then redirects to /login
import { AANMELDEN_MISLUKT_LOOP, AUTH_VEREIST } from "./authMessages";

const REDIRECT_WINDOW_MS = 60000;
const MAX_REDIRECTS = 2;

// Levensduur van een bewaarde bestemming. sessionStorage overleeft een refresh
// binnen hetzelfde tabblad, en de key wordt alleen gewist bij een expliciete
// logout. Zonder vervaltijd stuurt een pagina uit een vórige sessie (bv.
// /assets, waar de sessie ooit verliep) je bij de vólgende login daarheen,
// terwijl je gewoon op het inlogscherm begon. Een deeplink volgt normaal
// binnen seconden op de login, dus 5 minuten is ruim.
const POST_LOGIN_REDIRECT_TTL_MS = 5 * 60 * 1000;

// Paden waarheen navigeren ná een geslaagde login zinloos of schadelijk is:
// /login veroorzaakt zelf-navigatie (navigate() naar de huidige route is een
// stille no-op, waardoor de gebruiker op het inlogscherm blijft staan) en
// /forbidden is een doodlopend eind.
const INVALID_REDIRECT_PATHS = new Set(["/login", "/forbidden"]);

// Accepteert uitsluitend interne, absolute paden. Weert zelf-navigatie én
// open-redirects: "//evil.com" en "/\evil.com" worden door de browser als
// protocol-relatieve URL naar een VREEMDE origin opgelost, dus een kale
// startsWith("/") is hier niet genoeg.
export function isSafeInternalPath(path) {
  if (typeof path !== "string") return false;
  const trimmed = path.trim();
  if (!trimmed) return false;
  if (trimmed[0] !== "/") return false; // relatief of absolute URL
  if (trimmed[1] === "/" || trimmed[1] === "\\") return false; // //evil.com
  if (trimmed.includes("://")) return false;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F]/.test(trimmed)) return false; // control chars
  const pathname = trimmed.split(/[?#]/)[0]; // /login?reason=x -> /login
  return !INVALID_REDIRECT_PATHS.has(pathname);
}

// Bewaar de bestemming mét tijdstempel. Eén schrijver, zodat elke bewaarde
// bestemming gegarandeerd een leeftijd heeft die bij het lezen te toetsen is.
export function storePostLoginRedirect(path) {
  try {
    if (!isSafeInternalPath(path)) return false;
    sessionStorage.setItem(
      "POST_LOGIN_REDIRECT",
      JSON.stringify({ path, ts: Date.now() })
    );
    return true;
  } catch {
    return false;
  }
}

export function redirectToLoginWithError(message) {
  try {
    sessionStorage.setItem("AUTH_ERROR_MSG", message || AUTH_VEREIST);
    const here = window.location.pathname + window.location.search;
    // Alleen bewaren als het een zinvol doel is. Vanaf /login zou dit anders
    // "/login" wegschrijven, wat na de login tot zelf-navigatie leidt. De
    // bestaande (mogelijk waardevolle) deeplink blijft dan liever staan.
    storePostLoginRedirect(here);
  } catch {}

  // Anti-loop-guard: tel redirects binnen een tijdvenster. Bij te veel pogingen
  // stoppen we met redirecten en tonen we de unauthorized-staat op /login.
  let loopDetected = false;
  try {
    const now = Date.now();
    let counter = { n: 0, ts: now };
    const raw = sessionStorage.getItem("AUTH_REDIRECT_COUNT");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.n === "number" && typeof parsed.ts === "number") {
        counter = parsed;
      }
    }
    if (now - counter.ts > REDIRECT_WINDOW_MS) {
      counter = { n: 0, ts: now };
    }
    counter.n += 1;
    sessionStorage.setItem("AUTH_REDIRECT_COUNT", JSON.stringify(counter));

    if (counter.n >= MAX_REDIRECTS) {
      loopDetected = true;
      sessionStorage.setItem("AUTH_LOOP_DETECTED", "1");
      sessionStorage.setItem("AUTH_ERROR_MSG", AANMELDEN_MISLUKT_LOOP);
    }
  } catch {}

  if (loopDetected) {
    // Alleen navigeren als we nog niet op /login staan; anders niets doen (loop stoppen).
    if (typeof window !== "undefined" && window.location.pathname !== "/login") {
      window.location.assign("/login");
    }
    return;
  }

  window.location.assign("/login");
}

// Redirect to a forbidden page with an optional message
export function redirectToForbiddenWithError(message) {
  try {
    if (message) sessionStorage.setItem("AUTH_ERROR_MSG", message);
  } catch {}
  window.location.assign("/forbidden");
}

export function consumeAuthErrorMessage() {
  try {
    const msg = sessionStorage.getItem("AUTH_ERROR_MSG");
    if (msg) sessionStorage.removeItem("AUTH_ERROR_MSG");
    return msg;
  } catch {
    return null;
  }
}

export function consumePostLoginRedirect() {
  try {
    const raw = sessionStorage.getItem("POST_LOGIN_REDIRECT");
    // Altijd consumeren, ook als de waarde ongeldig of verlopen is: anders
    // blijft een vervuilde waarde staan en herhaalt het probleem zich bij de
    // volgende poging. Zo repareert een bestaande sessie zichzelf.
    if (raw !== null) sessionStorage.removeItem("POST_LOGIN_REDIRECT");
    if (!raw) return null;

    let path = null;
    let ts = null;
    if (raw[0] === "{") {
      const parsed = JSON.parse(raw);
      path = parsed?.path ?? null;
      ts = typeof parsed?.ts === "number" ? parsed.ts : null;
    } else {
      // Oud formaat (kale string) uit een sessie van vóór deze wijziging:
      // geen tijdstempel, dus niet te vertrouwen als verse bestemming.
      return null;
    }

    if (!isSafeInternalPath(path)) return null;
    // Verlopen bestemming hoort bij een eerdere sessie, niet bij deze login.
    if (ts === null || Date.now() - ts > POST_LOGIN_REDIRECT_TTL_MS) return null;
    return path;
  } catch {
    return null;
  }
}

// Logout-hygiëne: verwijder alle auth-gerelateerde sessionStorage-keys.
// Aanroepen vlak vóór instance.logoutRedirect(...).
export function clearAuthSessionState() {
  try {
    sessionStorage.removeItem("AUTH_ERROR_MSG");
    sessionStorage.removeItem("POST_LOGIN_REDIRECT");
    sessionStorage.removeItem("MSAL_CLAIMS_CHALLENGE");
    sessionStorage.removeItem("AUTH_REDIRECT_COUNT");
    sessionStorage.removeItem("AUTH_LOOP_DETECTED");
  } catch {}
  clearChatLocalState();
}

// De (verwijderde) chatfunctie bewaarde transcripts en de sessie-id in localStorage onder
// "chat:*". Die restanten blijven in bestaande browsers staan; bij logout ruimen we ze op zodat
// een volgende gebruiker op dezelfde browser ze niet ziet.
export function clearChatLocalState() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith("chat:")) keys.push(k);
    }
    keys.forEach((k) => {
      try { localStorage.removeItem(k); } catch {}
    });
  } catch {}
}
